/**
 *
 * @module logger
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/logging.md
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/dust.md
 * @see https://github.bamtech.co/fed-core/browser-sdk/blob/master/docs/logging/Logging.md
 *
 */

import Check from 'check-types';
import LogSink from './logSink';
// import Metric from './metric';
import LogEvent from './logEvent';
import LogLevel from './logLevel';
import ConsoleSink from './sinks/consoleSink';

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class Logger
 * @desc A logging system that can represent both messages and metric events,
 * and push them to various Sinks (logging output locations). Each sink is responsible
 * for (optionally) processing the event and outputting it. Examples of sinks include
 * the platform console, AWS, files on disk, etc.
 *
 */
export default class Logger {

    constructor() {

        /**
         *
         * @access public
         * @type {LogSink[]}
         *
         */
        this.sinks = [];

    }

    /**
     *
     * @access public
     * @param {String} source
     * @param {Metric|String} metricOrMessage
     * @param {Boolean} [isPublic=false]
     * @returns {Promise<Void>}
     *
     */
    log(source, metricOrMessage, isPublic = false) {

        return this.logWithOverrideLevel(source, metricOrMessage, isPublic);

    }

    /**
     *
     * @access private
     * @param {LogSink} sink
     * @returns {Promise<Void>}
     *
     */
    addSink(sink) {

        const errorMessage = {
            method: 'Logger.addSink(sink)',
            get sink() {
                return `${this.method} needs LogSink instance`;
            }
        };

        instanceStrict(sink, LogSink, errorMessage.sink);

        return new Promise((resolve) => {

            if (!this.sinks.includes(sink)) {
                this.sinks.push(sink);
            }

            resolve();

        });

    }

    /**
     *
     * @access private
     * @param {LogSink} sink
     * @returns {Promise<Void>}
     *
     */
    removeSink(sink) {

        const errorMessage = {
            method: 'Logger.removeSink(sink)',
            get sink() {
                return `${this.method} needs LogSink instance`;
            }
        };

        instanceStrict(sink, LogSink, errorMessage.sink);

        return new Promise((resolve) => {

            this.sinks = this.sinks.filter((s) => s !== sink);

            resolve();

        });

    }

    /*# region: public non-api */

    /**
     *
     * @function none
     * @memberOf {Logger}
     * @desc shorthand for #log with level override, takes the same arguments
     *
     */

    /**
     *
     * @function trace
     * @memberOf {Logger}
     * @desc shorthand for #log with level override
     *
     */

    /**
     *
     * @function debug
     * @memberOf {Logger}
     * @desc shorthand for #log with level override
     *
     */

    /**
     *
     * @function info
     * @memberOf {Logger}
     * @desc shorthand for #log with level override
     *
     */

    /**
     *
     * @function warn
     * @memberOf {Logger}
     * @desc shorthand for #log with level override
     *
     */

    /**
     *
     * @function error
     * @memberOf {Logger}
     * @desc shorthand for #log with level override
     *
     */

    /**
     *
     * @param {Boolean} [state=true] whether to enable the console sink
     * @returns {Promise<Void>}
     *
     */
    console(state = true) {

        return new Promise((resolve) => {

            this.sinks = this.sinks.filter((sink) => {
                return !(sink instanceof ConsoleSink);
            });

            if (state) {
                this.sinks.push(new ConsoleSink(LogLevel.none));
            }

            return resolve();

        });

    }

    /*# region: private */

    /**
     *
     * @access private
     * @param {String} source
     * @param {Metric|String} metricOrMessage
     * @param {Boolean} [isPublic=false]
     * @param {LogLevel} [overrideLevel=this.baseLogLevel]
     * @returns {Promise<Void>}
     *
     */
    logWithOverrideLevel(source, metricOrMessage, isPublic = false, overrideLevel = this.baseLogLevel) {

        // if there is only a source and no message, treat the source as the message
        if (typeof source === 'string' && typeof metricOrMessage === 'undefined') {
            return this.logMessage('', source, isPublic, overrideLevel);
        }

        if (typeof metricOrMessage === 'string') {
            return this.logMessage(source, metricOrMessage, isPublic, overrideLevel);
        }

        if (overrideLevel) {
            metricOrMessage.level = overrideLevel;
        }

        return this.logMetric(source, metricOrMessage);

    }

    /**
     *
     * @access private
     * @param {*} source
     * @param {Metric} metric
     * @returns {Promise<Void>}
     *
     */
    logMetric(source, metric) {

        return new Promise((resolve) => {

            const { isPublic, level, name, extraData } = metric;

            const event = new LogEvent(
                isPublic,
                typeof level === 'undefined' ? LogLevel.none : level,
                source,
                name,
                extraData
            );

            for (const sink of this.sinks) {
                sink.log(event);
            }

            resolve();

        });

    }

    /**
     *
     * @access private
     * @param {*} source
     * @param {String} message
     * @param {Boolean} isPublic
     * @param {LogLevel} level
     * @returns {Promise<Void>}
     *
     */
    logMessage(source, message, isPublic, level) {

        return new Promise((resolve) => {

            const event = new LogEvent(
                isPublic,
                level === undefined ? LogLevel.none : level,
                source,
                message
            );

            for (const sink of this.sinks) {
                sink.log(event);
            }

            resolve();

        });

    }

}

/**
 *
 * @desc Adds shortcut functions for Logger#log by the name of the LogLevel
 * e.g. Logger#warn
 *
 */
for (const level of Object.keys(LogLevel)) {

    /** @type {String} level (not the LogLevel, but the name of it) */

    Logger.prototype[level] = function (source, metricOrMessage, isPublic = false) {
        return this.logWithOverrideLevel(source, metricOrMessage, isPublic, LogLevel[level]);
    };

}



// WEBPACK FOOTER //
// ./src/logging/logger.js